import React, { useState, useEffect } from 'react';
import { TOKENS } from '../tokens';
import api from '../api/client';
import { Users, AlertTriangle, CheckSquare, Clock } from 'lucide-react';

export function ManagerAllocationsScreen() {
  const [allocations, setAllocations] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [allocRes, tsRes] = await Promise.all([
        api.get('/team/allocations'),
        api.get('/team/timesheets')
      ]);
      setAllocations(allocRes.data);
      setTimesheets(tsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const approveTimesheet = async (id) => {
    try {
      await api.put(`/team/timesheets/${id}/approve`);
      fetchData();
    } catch (err) {
      alert("Error approving timesheet: " + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div style={{ padding: 24, paddingBottom: 100 }}>
      <h2 style={{ margin: '0 0 24px 0', fontFamily: 'Fraunces', color: TOKENS.navyDeep, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Users color={TOKENS.gold} /> Team Allocations & Timesheets
      </h2>

      {loading ? <p>Loading...</p> : (
        <div style={{ display: 'flex', gap: 24, flexDirection: 'column' }}>
          
          {/* Allocations Section */}
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, border: '1px solid #eee' }}>
            <h3 style={{ margin: '0 0 16px 0', color: TOKENS.navyDeep, borderBottom: '2px solid #eee', paddingBottom: 8 }}>Resource Allocation</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8f9fa', textAlign: 'left', color: '#666', fontSize: 14 }}>
                  <th style={{ padding: 12 }}>Employee Name</th>
                  <th style={{ padding: 12 }}>Total Allocation</th>
                  <th style={{ padding: 12 }}>Project Breakdown</th>
                </tr>
              </thead>
              <tbody>
                {allocations.map(emp => (
                  <tr key={emp.user_id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: 12, fontWeight: 600 }}>{emp.name}</td>
                    <td style={{ padding: 12 }}>
                      <div style={{ 
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        color: emp.total_allocation > 100 ? '#D32F2F' : TOKENS.navyDeep,
                        fontWeight: 700
                      }}>
                        {emp.total_allocation || 0}%
                        {emp.total_allocation > 100 && <AlertTriangle size={14} />}
                      </div>
                    </td>
                    <td style={{ padding: 12, fontSize: 13, color: '#555' }}>
                      {emp.projects?.map((p, i) => (
                        <div key={i} style={{ marginBottom: 4 }}>
                          • {p.name}: <span style={{ fontWeight: 600 }}>{p.allocation_pct}%</span> 
                          <span style={{ color: '#888', marginLeft: 8 }}>({p.role_on_project || 'Member'})</span>
                        </div>
                      ))}
                      {!emp.projects?.length && <span>No active projects</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Timesheets Section */}
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, border: '1px solid #eee' }}>
            <h3 style={{ margin: '0 0 16px 0', color: TOKENS.navyDeep, borderBottom: '2px solid #eee', paddingBottom: 8 }}>Submitted Timesheets</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
              {timesheets.map(ts => (
                <div key={ts.id} style={{ border: '1px solid #eee', borderRadius: 12, padding: 16, background: ts.approved_by ? '#F8FCF9' : '#fff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, color: TOKENS.navyDeep }}>{ts.user_name}</div>
                    <div style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Clock size={12} /> Wk: {ts.week_start}
                    </div>
                  </div>
                  <div style={{ fontSize: 14, color: '#555', marginBottom: 12 }}>
                    <div>Project: <b>{ts.project_name}</b></div>
                    <div>Hours logged: <b>{ts.hours}h</b></div>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {ts.approved_by ? (
                      <span style={{ fontSize: 12, color: '#1E8E3E', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <CheckSquare size={14} /> Approved
                      </span>
                    ) : (
                      <button 
                        onClick={() => approveTimesheet(ts.id)}
                        style={{ background: TOKENS.gold, color: TOKENS.navyDeep, border: 'none', padding: '6px 12px', borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}
                      >
                        Approve
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {timesheets.length === 0 && <p style={{ color: '#888' }}>No timesheets submitted.</p>}
            </div>
          </div>
          
        </div>
      )}
    </div>
  );
}
